import { graphql, readInlineData } from 'react-relay';
import { createStore, Store, StoreActionApi } from 'react-sweet-state';

import { filterFalsyStrings, filterNull } from '@townsquare/filter-type';

import { storeDirectoryView$key } from './__generated__/storeDirectoryView.graphql';
import { DirectoryViewDetails } from './types';

const initialState: DirectoryViewDetails = {
	id: undefined,
	uuid: undefined,
	name: undefined,
	entityType: undefined,
	storedTql: undefined,
	projectSort: undefined,
	goalSort: undefined,
	uiViewType: undefined,
	timelineDateSpan: undefined,
	isFavourite: undefined,
	favouriteCount: undefined,
	isDefault: undefined,
	directoryViewColumns: undefined,
	fragment: undefined,
};

export const directoryViewStore: Store<
	DirectoryViewDetails,
	{
		setDirectoryView: (
			directoryViewRef: storeDirectoryView$key | undefined | null,
		) => ({ setState }: StoreActionApi<DirectoryViewDetails>) => DirectoryViewDetails;
		clearDirectoryView: () => ({ setState }: StoreActionApi<DirectoryViewDetails>) => void;
	}
> = createStore({
	initialState,
	actions: {
		setDirectoryView:
			(
				directoryViewRef: storeDirectoryView$key | undefined | null,
			): (({ setState }: StoreActionApi<DirectoryViewDetails>) => DirectoryViewDetails) =>
			({ setState }) => {
				if (directoryViewRef) {
					const data = readInlineData(
						graphql`
							fragment storeDirectoryView on DirectoryView @inline {
								id
								uuid
								name
								storedTql
								entityType
								sort {
									projectSort
									goalSort
								}
								uiViewType
								uiMetadata {
									timelineDateSpan
								}
								isFavourite
								favouriteCount
								isDefault
								creator {
									aaid
								}
								directoryViewColumns {
									... on StandardDirectoryViewColumn {
										__typename
										id
										name
									}
									... on CustomDirectoryViewColumn {
										__typename
										id
										uuid
									}
								}
								...DirectoryFavoriteViewButton
								...DirectoryManageViewDialog
							}
						`,
						directoryViewRef,
					);

					const newState: DirectoryViewDetails = {
						id: data.id,
						uuid: data.uuid,
						name: data.name,
						entityType: data.entityType ?? undefined,
						storedTql: data.storedTql ?? undefined,
						projectSort: data.sort?.projectSort?.filter(filterNull) ?? undefined,
						goalSort: data.sort?.goalSort?.filter(filterNull) ?? undefined,
						uiViewType: data.uiViewType ?? undefined,
						timelineDateSpan: data.uiMetadata?.timelineDateSpan ?? undefined,
						fragment: data,
						isFavourite: data.isFavourite ?? undefined,
						favouriteCount: data.favouriteCount ?? 0,
						isDefault: data.isDefault ?? undefined,
						creatorAaid: data.creator?.aaid ?? undefined,
						directoryViewColumns: data.directoryViewColumns
							?.map((column) => {
								switch (column?.__typename) {
									case 'StandardDirectoryViewColumn':
										return column.name;
									case 'CustomDirectoryViewColumn':
										return column.uuid;
									default:
										return null;
								}
							})
							?.filter(filterFalsyStrings),
					};

					setState(newState);

					return newState;
				}

				// Reset the state
				// Yes we need to do this because setState does clever merging
				// and in case of {} prevoius state is not cleared
				setState(initialState);

				return {};
			},
		clearDirectoryView:
			(): (({ setState }: StoreActionApi<DirectoryViewDetails>) => void) =>
			({ setState }) => {
				setState(initialState);
			},
	},
	name: 'directory-view-store',
});
